import {
  Combobox,
  Input,
  InputBase,
  InputBaseProps,
  Stack,
  Text,
  useCombobox,
} from "@mantine/core";
import { useLoaderData } from "@remix-run/react";
import { useEffect, useState } from "react";

import { useTranslation } from "~/hooks/use-typed-translation";
import { ComboboxItemPrice } from "~/types";
import { formatCurrency } from "~/utils/format-currency";

function SelectOption({ label, price, duration, category }: ComboboxItemPrice) {
  const { i18n } = useTranslation();
  const loaderData = useLoaderData<{ currency: string }>();
  const currency = loaderData?.currency as string;

  return (
    <Stack gap={3}>
      <Text fz={14}>
        {label + " - " + formatCurrency(price, i18n.language, currency)}
      </Text>
      <Text fz={12} c="dimmed" tt={"capitalize"}>
        {category + " - "}
        {duration} mins
      </Text>
    </Stack>
  );
}

interface ConsultationServiceSelectProps
  extends Omit<InputBaseProps, "onChange"> {
  data?: ComboboxItemPrice[];
  placeholder?: string;
  onChange?: (value: string | null) => void;
  value?: string | null;
  error?: React.ReactNode;
}

export function ConsultationServiceSelect({
  data,
  placeholder,
  rightSection,
  onChange,
  value: controlledValue,
  error,
  ...rest
}: ConsultationServiceSelectProps) {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [internalValue, setInternalValue] = useState<string | null>(
    controlledValue ?? null
  );

  // Sync internal state with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  const selectedOption = data?.find(
    item => item.value === (controlledValue ?? internalValue)
  );

  const options = data?.map(item => (
    <Combobox.Option value={item.value} key={item.value}>
      <SelectOption {...item} />
    </Combobox.Option>
  ));

  const handleOptionSubmit = (val: string) => {
    setInternalValue(val);
    onChange?.(val);
    combobox.closeDropdown();
  };

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={rightSection ? rightSection : <Combobox.Chevron />}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          multiline
          error={error}
          {...rest}>
          {selectedOption ? (
            <SelectOption {...selectedOption} />
          ) : (
            <Input.Placeholder>
              {placeholder ? placeholder : t("select-service")}
            </Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
